import { canvasFlatten, canvasThreshold, resizeCanvas } from './canvas.utils'

/**
 * Create a byte stream based on commands for ESC/POS printers
 */
export class EscPosEncoder {
  private buffer: (number | Uint8Array)[] = []

  constructor() {
    this.reset()
  }

  /**
   * Reset the state of the object
   *
   */
  private reset(): void {
    this.buffer = []
  }

  /**
   * Add commands to the buffer
   *
   * @param {array} value  And array of numbers, arrays, buffers or Uint8Array to add to the buffer
   */
  private queue(value: (number | Uint8Array)[]): void {
    value.forEach(item => this.buffer.push(item))
  }

  /**
   * Initialize the printer
   *
   * @return {object} Return the object, for easy chaining commands
   */
  initialize(): this {
    this.queue([0x1b, 0x40])

    return this
  }

  /**
   * Print a newline
   *
   * @return {object} Return the object, for easy chaining commands
   */
  newline(count: number = 1): this {
    // Add the specified number of newlines
    for (let i = 0; i < count; i++) {
      this.queue([0x0a, 0x0d])
    }

    return this
  }

  /**
   * Change text alignment
   *
   * @param  {string} value   left, center or right
   * @return {object} Return the object, for easy chaining commands
   */
  align(value: 'left' | 'center' | 'right'): this {
    const alignments = {
      left: 0x00,
      center: 0x01,
      right: 0x02
    }
    if (value in alignments) {
      this.queue([0x1b, 0x61, alignments[value]])
    } else {
      throw new Error('Unknown alignment')
    }

    return this
  }

  /**
   * Image - optimized version for HTMLCanvasElement
   *
   * @param  {HTMLCanvasElement} canvas  Canvas element to be printed
   * @param  {number} width  width of the image on the printer
   * @param  {number} height  height of the image on the printer
   * @return {object} Return the object, for easy chaining commands
   */
  image(canvas: HTMLCanvasElement, width: number, height: number): this {
    if (width % 8 !== 0) {
      throw new Error('Width must be a multiple of 8')
    }
    if (height % 8 !== 0) {
      throw new Error('Height must be a multiple of 8')
    }

    const context = resizeCanvas(canvas, width, height)
    let image = context.getImageData(0, 0, width, height)
    image = canvasFlatten(image, [0xff, 0xff, 0xff])
    image = canvasThreshold(image, 128)

    const getPixel = (x: number, y: number): number => (image.data[(width * y + x) * 4] > 0 ? 0 : 1)

    const bytes = new Uint8Array((width * height) >> 3)
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x = x + 8) {
        const i = y * (width >> 3) + (x >> 3)
        bytes[i] =
          (getPixel(x + 0, y) << 7) |
          (getPixel(x + 1, y) << 6) |
          (getPixel(x + 2, y) << 5) |
          (getPixel(x + 3, y) << 4) |
          (getPixel(x + 4, y) << 3) |
          (getPixel(x + 5, y) << 2) |
          (getPixel(x + 6, y) << 1) |
          getPixel(x + 7, y)
      }
    }
    let offset = 0
    const maxHeight = 2048
    const lineBytes = Math.round(width / 8)
    const chunkSize = Math.round(lineBytes * maxHeight)
    while (offset < bytes.length) {
      const chunk = bytes.slice(offset, offset + chunkSize)
      const chunkHeight = Math.round(chunk.length / lineBytes)
      this.queue([
        0x1d,
        0x76,
        0x30,
        0x00,
        (width >> 3) & 0xff,
        ((width >> 3) >> 8) & 0xff,
        chunkHeight & 0xff,
        (chunkHeight >> 8) & 0xff,
        chunk
      ])
      offset += chunkSize
    }

    return this
  }

  imageEsc(canvas: HTMLCanvasElement, width: number, height: number): this {
    if (width % 8 !== 0) {
      throw new Error('Width must be a multiple of 8')
    }
    if (height % 8 !== 0) {
      throw new Error('Height must be a multiple of 8')
    }

    const context = resizeCanvas(canvas, width, height)
    let image = context.getImageData(0, 0, width, height)
    image = canvasFlatten(image, [0xff, 0xff, 0xff])
    image = canvasThreshold(image, 128)

    const getPixel = (x: number, y: number): number => (image.data[(width * y + x) * 4] > 0 ? 0 : 1)

    this.queue([0x1b, 0x33, 0x10])
    for (let y = 0; y < height; y = y + 8) {
      const bytes = new Uint8Array(width)
      for (let x = 0; x < width; x++) {
        bytes[x] =
          (getPixel(x, y + 0) << 7) |
          (getPixel(x, y + 1) << 6) |
          (getPixel(x, y + 2) << 5) |
          (getPixel(x, y + 3) << 4) |
          (getPixel(x, y + 4) << 3) |
          (getPixel(x, y + 5) << 2) |
          (getPixel(x, y + 6) << 1) |
          getPixel(x, y + 7)
      }
      this.queue([0x1b, 0x2a, 0x00, width & 0xff, (width >> 8) & 0xff, bytes])
    }
    this.queue([0x1b, 0x32])

    return this
  }

  /**
   * Cut paper
   *
   * @param  {string} value full or partial. When not specified a full cut will be assumed
   * @return {object} Return the object, for easy chaining commands
   */
  cut(value?: 'full' | 'partial'): this {
    let data = 0x00
    if (value === 'partial') {
      data = 0x01
    }
    this.queue([0x1d, 0x56, data])

    return this
  }

  /**
   * Open cash drawer
   *
   * @returns {object} Return the object, for easy chaining commands
   */
  openCashDrawer(): this {
    this.queue([0x1b, 0x70, 0x00])
    this.queue([0x1b, 0x70, 0x01])

    return this
  }

  /**
   * Beep the printer
   * Last two bytes are the number of beeps and the duration
   *
   * @returns {object} Return the object, for easy chaining commands
   */
  beep() {
    this.queue([0x1b, 0x42, 0x02, 0x01])

    return this
  }

  /**
   * Get status of the printer
   *
   * * @returns {object} Return the object, for easy chaining commands
   */
  status(): this {
    this.queue([0x10, 0x04, 0x01])

    return this
  }

  /**
   * Add raw printer commands
   *
   * @param  {array}           data   raw bytes to be included
   * @return {object}          Return the object, for easy chaining commands
   */
  raw(data: (number | Uint8Array)[]): this {
    this.queue(data)

    return this
  }

  /**
   * Encode all previous commands
   *
   * @return {Uint8Array} Return the encoded bytes
   */
  encode(): Uint8Array {
    let length = 0
    this.buffer.forEach(item => {
      if (typeof item === 'number') {
        length++
      } else {
        length += item.length
      }
    })
    const result = new Uint8Array(length)
    let index = 0
    this.buffer.forEach(item => {
      if (typeof item === 'number') {
        result[index] = item
        index++
      } else {
        result.set(item, index)
        index += item.length
      }
    })
    this.reset()

    return result
  }

  /**
   * Encode all previous commands as Base64 string
   *
   * @return {string} Return the encoded bytes as Base64 string
   */
  encodeAsBase64(): string {
    const uint8Array = this.encode()
    let binaryString = ''
    for (let i = 0; i < uint8Array.length; i++) {
      binaryString += String.fromCharCode(uint8Array[i])
    }
    return btoa(binaryString)
  }
}
